// import React, { useState } from 'react'
import { 
  HeartIcon, 
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  ClockIcon,
  ServerIcon,
  CpuChipIcon,
  CircleStackIcon
} from '@heroicons/react/24/outline'

interface HealthCheck {
  service: string
  status: 'HEALTHY' | 'DEGRADED' | 'UNHEALTHY'
  response_time: number
  last_check: string
  uptime: number
  error?: string
}

const SystemHealth: React.FC = () => {
  // Mock data - replace with real API call
  const mockHealthChecks: HealthCheck[] = [
    {
      service: 'Authentication Service',
      status: 'HEALTHY',
      response_time: 45,
      last_check: '2024-01-15T10:30:00Z',
      uptime: 99.9
    },
    {
      service: 'User Management',
      status: 'HEALTHY',
      response_time: 32,
      last_check: '2024-01-15T10:30:00Z',
      uptime: 99.8
    },
    {
      service: 'Organizations Service',
      status: 'HEALTHY',
      response_time: 28,
      last_check: '2024-01-15T10:30:00Z',
      uptime: 99.7
    },
    {
      service: 'Projects Service',
      status: 'DEGRADED',
      response_time: 250,
      last_check: '2024-01-15T10:30:00Z',
      uptime: 98.5,
      error: 'High response time detected'
    },
    {
      service: 'Billing Service',
      status: 'HEALTHY',
      response_time: 56,
      last_check: '2024-01-15T10:30:00Z',
      uptime: 99.9
    },
    {
      service: 'Notifications Service',
      status: 'HEALTHY',
      response_time: 41,
      last_check: '2024-01-15T10:30:00Z',
      uptime: 99.6
    },
    {
      service: 'Database (MySQL)',
      status: 'HEALTHY',
      response_time: 12,
      last_check: '2024-01-15T10:30:00Z',
      uptime: 99.95
    },
    {
      service: 'Redis Cache',
      status: 'HEALTHY',
      response_time: 8,
      last_check: '2024-01-15T10:30:00Z',
      uptime: 99.9
    },
    {
      service: 'External APIs',
      status: 'UNHEALTHY',
      response_time: 0,
      last_check: '2024-01-15T10:25:00Z',
      uptime: 95.2,
      error: 'Connection timeout to third-party service'
    }
  ]

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'HEALTHY': return 'bg-green-100 text-green-800'
      case 'DEGRADED': return 'bg-yellow-100 text-yellow-800'
      case 'UNHEALTHY': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'HEALTHY': return <CheckCircleIcon className="h-5 w-5 text-green-600" />
      case 'DEGRADED': return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600" />
      case 'UNHEALTHY': return <XCircleIcon className="h-5 w-5 text-red-600" />
      default: return <ClockIcon className="h-5 w-5 text-gray-600" />
    }
  }

  const getServiceIcon = (service: string) => {
    if (service.includes('Database')) return <CircleStackIcon className="h-6 w-6" />
    if (service.includes('Cache')) return <CpuChipIcon className="h-6 w-6" />
    return <ServerIcon className="h-6 w-6" />
  }

  const overallHealth = () => {
    const healthyCount = mockHealthChecks.filter(h => h.status === 'HEALTHY').length
    const totalCount = mockHealthChecks.length
    return Math.round((healthyCount / totalCount) * 100)
  }

  const averageResponseTime = () => {
    const total = mockHealthChecks.reduce((sum, h) => sum + h.response_time, 0)
    return Math.round(total / mockHealthChecks.length)
  }

  const averageUptime = () => {
    const total = mockHealthChecks.reduce((sum, h) => sum + h.uptime, 0)
    return (total / mockHealthChecks.length).toFixed(2)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">System Health</h1>
          <p className="text-gray-600">Monitor the health and performance of all system components</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-sm text-gray-500">Last updated: {new Date().toLocaleTimeString()}</div>
          <button className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700">
            Refresh All
          </button>
        </div>
      </div>

      {/* Overall Health Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <HeartIcon className="h-6 w-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">Overall Health</p>
              <p className="text-2xl font-bold text-gray-900">{overallHealth()}%</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckCircleIcon className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">Healthy Services</p>
              <p className="text-2xl font-bold text-gray-900">
                {mockHealthChecks.filter(h => h.status === 'HEALTHY').length}/{mockHealthChecks.length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ClockIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">Avg Response Time</p>
              <p className="text-2xl font-bold text-gray-900">{averageResponseTime()}ms</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center">
                <span className="text-xs text-white font-bold">%</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">Avg Uptime</p>
              <p className="text-2xl font-bold text-gray-900">{averageUptime()}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Service Health Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {mockHealthChecks.map((check, index) => (
          <div key={index} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center">
                <div className="flex-shrink-0 text-gray-600">
                  {getServiceIcon(check.service)}
                </div>
                <h3 className="ml-3 text-lg font-medium text-gray-900">{check.service}</h3>
              </div>
              <div className="flex items-center">
                {getStatusIcon(check.status)}
              </div>
            </div>
            
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">Status</span>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(check.status)}`}>
                  {check.status}
                </span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">Response Time</span>
                <span className="text-sm font-medium text-gray-900">{check.response_time}ms</span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">Uptime</span>
                <span className="text-sm font-medium text-gray-900">{check.uptime}%</span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">Last Check</span>
                <span className="text-sm text-gray-900">
                  {new Date(check.last_check).toLocaleTimeString()}
                </span>
              </div>
              
              {check.error && (
                <div className="mt-3 p-3 bg-red-50 rounded-md">
                  <p className="text-sm text-red-800">{check.error}</p>
                </div>
              )}
            </div>
            
            <div className="mt-4 pt-4 border-t border-gray-200">
              <button className="w-full text-blue-600 hover:text-blue-800 text-sm font-medium">
                View Details
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* System Metrics */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-lg font-medium text-gray-900 mb-6">System Metrics</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-gray-900">24h</div>
            <div className="text-sm text-gray-500">Average Uptime</div>
            <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
              <div className="bg-green-600 h-2 rounded-full" style={{ width: '99%' }}></div>
            </div>
          </div>
          
          <div className="text-center">
            <div className="text-3xl font-bold text-gray-900">2.5s</div>
            <div className="text-sm text-gray-500">Average Response</div>
            <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
              <div className="bg-blue-600 h-2 rounded-full" style={{ width: '75%' }}></div>
            </div>
          </div>
          
          <div className="text-center">
            <div className="text-3xl font-bold text-gray-900">0</div>
            <div className="text-sm text-gray-500">Critical Alerts</div>
            <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
              <div className="bg-green-600 h-2 rounded-full" style={{ width: '100%' }}></div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Alerts */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Alerts</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-md">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mr-3" />
              <div>
                <p className="text-sm font-medium text-gray-900">High response time detected</p>
                <p className="text-xs text-gray-500">Projects Service - 5 minutes ago</p>
              </div>
            </div>
            <button className="text-yellow-600 hover:text-yellow-800 text-sm">
              Acknowledge
            </button>
          </div>
          
          <div className="flex items-center justify-between p-3 bg-red-50 rounded-md">
            <div className="flex items-center">
              <XCircleIcon className="h-5 w-5 text-red-600 mr-3" />
              <div>
                <p className="text-sm font-medium text-gray-900">External API connection failed</p>
                <p className="text-xs text-gray-500">External APIs - 15 minutes ago</p>
              </div>
            </div>
            <button className="text-red-600 hover:text-red-800 text-sm">
              Investigate
            </button>
          </div>
          
          <div className="text-center py-4">
            <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
              View All Alerts
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SystemHealth 